import React, { useEffect, useState, useCallback } from 'react';
import { useAxios } from '../../Helpers/AxiosHelper';
import { useTranslation } from 'react-i18next';
import { DataTable, Column, SCIcon, ReactCountryFlag, ProgressBar } from '../../Common/Imports';
import { ApiResponse, Brands_EXT, ConfigMailTemplate_EXT, Countries_EXT } from '../../Models/Classes';
import ConfigMailTemplatesNew from './ConfigMailTemplates_New';
import ConfigMailTemplatesEdit from './ConfigMailTemplates_Edit';
import { useToast } from '../../Helpers/ToastHelper';

interface ConfigMailTemplatesListProps {
    stakeholderId: number;
}

export const ConfigMailTemplatesList: React.FC<ConfigMailTemplatesListProps> = ({ stakeholderId }) => {
    const AxiosHelper = useAxios();
    const { showToast } = useToast();
    const { t } = useTranslation();
    const [TableData, SetTableData] = useState<ConfigMailTemplate_EXT[] | undefined>();
    const [TableLoader, SetTableLoader] = useState<boolean>(false);
    const [CountriesData, setCountriesData] = useState<Countries_EXT[] | undefined>();
    const [BrandsData, setBrandsData] = useState<Brands_EXT[] | undefined>();
    const [EditData, setEditData] = useState<ConfigMailTemplate_EXT | undefined>();
    const [CreateNewData, setCreateNewData] = useState<boolean>(false);
    const [Types] = useState([{
        label: "Customer Email",
        id: 0,
        data: 0
    },
    {
        label: "Provider Email",
        id: 1,
        data: 1
    },
    {
        label: "Invoice Email",
        id: 2,
        data: 2
    }]);

    const GetData = useCallback(async () => {
        SetTableLoader(true);
        try {

            const resp = await AxiosHelper.get<ApiResponse<ConfigMailTemplate_EXT[]>>(`StakeholderConfigs/MailTemplates/GetList/${stakeholderId}`);
            if (resp.data.code == 0) {
                SetTableData(resp.data.data);
            }

            const resp2 = await AxiosHelper.get<ApiResponse<Countries_EXT[]>>(`Countries/GetList`);
            if (resp2.data.code === 0) {
                setCountriesData(resp2.data.data);
            }

            const resp3 = await AxiosHelper.get<ApiResponse<Brands_EXT[]>>(`Brand/GetList?StakeholderId=` + stakeholderId);
            if (resp3.data.code === 0) {
                setBrandsData(resp3.data.data);
            }
        } catch {
            showToast('error', 'Error', t("Common.Toasts.New.Error"));
        } finally {
            SetTableLoader(false);
        }
    }, [AxiosHelper, showToast, stakeholderId, t]);

    useEffect(() => {
        GetData();
    }, [GetData]);

    const openEdit = (data: ConfigMailTemplate_EXT) => {
        setEditData(data);
    }

    const editBody = (data: ConfigMailTemplate_EXT) => {
        return <SCIcon Name="Edit" Classes="text-secondary" Pointer OnClick={() => openEdit(data)} />;
    }

    return (
        <>
            {
                CreateNewData &&
                <ConfigMailTemplatesNew stakeholderId={stakeholderId} OnSave={() => { setCreateNewData(false); GetData() }} />
            }
            {
                EditData &&
                <ConfigMailTemplatesEdit IdTemplate={EditData.idTemplate} OnSave={() => { setEditData(undefined); GetData() }} />
            }
            <div className="col d-flex justify-content-end">
                <SCIcon Name="Add" Classes="text-primary" Pointer OnClick={() => setCreateNewData(true)}></SCIcon>
            </div>
            {
                TableLoader ?
                    <ProgressBar mode="indeterminate" style={{ height: '6px' }}></ProgressBar>
                    :
                    <DataTable
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        value={TableData}
                        size="small"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} records"
                        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                        stripedRows>
                        <Column field="idCountry"
                            header={t("Stakeholder.Configs_MailTemplate.Country")}
                            body={(e: ConfigMailTemplate_EXT) => {
                                const country = CountriesData?.find(m => m.idCountry === e.idCountry);
                                return <><ReactCountryFlag
                                    countryCode={country?.isO2 ?? ""}
                                    svg
                                    title={country?.name} // Ensure title matches the data label
                                    style={{ marginRight: "10px" }} />
                                    {country?.name || ""}</>;
                            }}
                            bodyClassName="table-cell-nowrap"/>
                        <Column field="idBrand"
                            header={t("Stakeholder.Configs_MailTemplate.Brand")}
                            body={(e: ConfigMailTemplate_EXT) => { return <p>{BrandsData?.find(m => m.idBrand == e.idBrand)?.name}</p> }}
                            bodyClassName="table-cell-nowrap"/>
                        <Column
                            field="provider"
                            header={t("Stakeholder.Configs_MailTemplate.Provider")}
                            bodyClassName="table-cell-nowrap"/>
                        <Column
                            field="type"
                            header={t("Stakeholder.Configs_MailTemplate.Type")}
                            body={(e: ConfigMailTemplate_EXT) => { return <p>{Types.find(m => m.id == e.type)?.label}</p> }}
                            bodyClassName="table-cell-nowrap"/>
                        <Column
                            body={(e: ConfigMailTemplate_EXT) => editBody(e)}
                            style={{ maxWidth: '50px!important', width: "50px" }}
                            bodyClassName="table-cell-nowrap" />
                    </DataTable>
            }
        </>
    );
};

export default ConfigMailTemplatesList;
